import { Component, ErrorInfo, ReactNode } from 'react'

interface RenderErrorBoundaryProps {
  children: ReactNode
  fallback: (error: Error, reset: () => void) => ReactNode
  onError?: (error: Error, info: ErrorInfo) => void
}

interface RenderErrorBoundaryState {
  error: Error | null
}

/**
 * Catches render-time errors from children & renders a fallback with a reset callback.
 * Resetting clears the error and remounts children from scratch, which re-fires any Apollo
 * queries they mount (errored results are never written to the cache).
 *
 * React error boundaries can only be implemented as class components.
 */
export class RenderErrorBoundary extends Component<
  RenderErrorBoundaryProps,
  RenderErrorBoundaryState
> {
  state: RenderErrorBoundaryState = { error: null }

  static getDerivedStateFromError(thrown: unknown): RenderErrorBoundaryState {
    return { error: thrown instanceof Error ? thrown : new Error(String(thrown)) }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.state.error, this.reset)
    }
    return this.props.children
  }
}
